/**
 * Cuerpo compartido del wizard de producto personalizado.
 * Usado por Dialog productivo (mode=create) y Preview sandbox (mode=preview).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Plus, RefreshCw, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ConfiguradorArbol,
  ConfiguradorArbolCampo,
  configuradorAPI,
  Deposito,
  opcionesAPI,
  Proveedor,
  proveedoresAPI,
  Sucursal,
  depositoSirveSucursal,
} from "@/lib/api";
import { emptyOnForbidden, isForbiddenError } from "@/lib/httpErrors";
import {
  campoTipoConOpciones,
  campoTipoNumerico,
  perfilIdForFabricaKey,
} from "./configuradorAdminHelpers";
import {
  formatArgentineMoneyPreview,
  formatArgentineMoneyTyping,
  toMoneyNumber,
} from "@/lib/parseArgentineDecimal";
import {
  asEvalCampos,
  campoManualOk,
  campoRequired,
  campoVisible,
  descripcionDisplayValue,
  formatDescripcionPart,
  joinDescripcionParts,
  KEY_FABRICA,
  KEY_OBS,
  KEY_PRECIO,
  KEY_PROVEEDOR_OTRAS,
  KEY_STOCK,
  KEY_SUCURSAL,
  MSG_CONFIGURADOR_CATALOG_EMPTY,
  MSG_CONFIGURADOR_CATALOG_FORBIDDEN,
  SPECIAL_KEYS,
  type ConfiguradorProductoFormMode,
  type EvalCampo,
  proveedoresParaOtrasFabricas,
} from "./configuradorProductoShared";
import { NumberField } from "@/components/ui/number-field";

export const inputClass =
  "w-full px-3 py-2 border border-input bg-input rounded-lg text-sm disabled:opacity-50";
export const labelClass = "block text-sm font-medium mb-1";

export type UseConfiguradorProductoFormArgs = {
  open: boolean;
  mode: ConfiguradorProductoFormMode;
  /** Incrementar para forzar re-fetch del árbol (preview tras CRUD). */
  catalogRevision?: number;
  /**
   * Si false (presupuesto diferido): no exigir/mostrar stock ni sucursal/depósito.
   * El stock se materializa al confirmar la venta.
   */
  includeStock?: boolean;
  /**
   * Mostrar «Nuevo proveedor» en Otras fábricas.
   * Default true solo en preview; en create pasa el gate de rol.
   */
  canCreateProveedor?: boolean;
};

export function useConfiguradorProductoForm({
  open,
  mode,
  catalogRevision = 0,
  includeStock = true,
  canCreateProveedor,
}: UseConfiguradorProductoFormArgs) {
  const isPreview = mode === "preview";
  // Opt-in estricto: solo true explícito. Preview (catálogo) sí muestra el botón.
  // Ausente en create → oculto (no default true / no «tiene Ventas»).
  const allowCreateProveedor =
    canCreateProveedor === true || (canCreateProveedor == null && isPreview);

  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [arbol, setArbol] = useState<ConfiguradorArbol | null>(null);
  const [selecciones, setSelecciones] = useState<Record<string, unknown>>({});
  const [evalCampos, setEvalCampos] = useState<EvalCampo[]>([]);
  const [rama, setRama] = useState<string | null>(null);

  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [proveedorId, setProveedorId] = useState<number>(0);
  const [showNewProveedor, setShowNewProveedor] = useState(false);

  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [depositos, setDepositos] = useState<Deposito[]>([]);
  const [sucursalId, setSucursalId] = useState(0);
  const [depositoId, setDepositoId] = useState(0);

  const [precioPreview, setPrecioPreview] = useState<{
    precio: string;
    ivaPorcentaje: string;
    precioFinalConIva: string;
  } | null>(null);

  const [clientErrors, setClientErrors] = useState<string[]>([]);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const evalSeq = useRef(0);
  const keepSelectionsOnReload = useRef(false);

  const arbolByKey = useMemo(() => {
    const map = new Map<string, ConfiguradorArbolCampo>();
    for (const c of arbol?.campos || []) map.set(c.key, c);
    return map;
  }, [arbol]);

  const fabricaOpciones = useMemo(
    () => arbolByKey.get(KEY_FABRICA)?.opciones || [],
    [arbolByKey],
  );

  const fabricaValue = String(selecciones[KEY_FABRICA] || "");
  const isFavre = fabricaValue === "favre";
  const isOtras = fabricaValue === "otras_fabricas";

  const evalByKey = useMemo(() => {
    const map = new Map<string, EvalCampo>();
    for (const c of evalCampos) map.set(c.key, c);
    return map;
  }, [evalCampos]);

  const technicalFields = useMemo(() => {
    const all = (arbol?.campos || []).filter(
      (c) => !SPECIAL_KEYS.has(c.key) && c.activo !== false,
    );
    // Membresía = PerfilCampo del perfil activo (eval scoped). No listar por
    // aplicaFavre ni por la tilde tecnico.
    return all
      .filter((c) => {
        const ev = evalByKey.get(c.key);
        if (!ev) return false;
        const enGrafo = ev.enGrafo ?? ev.en_grafo;
        if (enGrafo === false) return false;
        return campoVisible(ev);
      })
      .sort((a, b) => a.orden - b.orden || a.key.localeCompare(b.key));
  }, [arbol, evalByKey]);

  const availableDepositos = useMemo(
    () =>
      sucursalId
        ? depositos.filter((d) => depositoSirveSucursal(d, sucursalId))
        : depositos,
    [depositos, sucursalId],
  );

  const stockInicial = String(selecciones[KEY_STOCK] ?? "0");
  const stockNum = Number(String(stockInicial).replace(",", "."));
  const stockGtZero = Number.isFinite(stockNum) && stockNum > 0;

  const precioRaw = String(selecciones[KEY_PRECIO] ?? "");
  const observacion = String(selecciones[KEY_OBS] ?? "");

  const otrasProveedores = useMemo(
    () => proveedoresParaOtrasFabricas(proveedores),
    [proveedores],
  );

  const descripcionPreview = useMemo(() => {
    // Mismo helper que BE generate_descripcion: « | », etiqueta si el flag está on.
    // FAVRE y Otras no anteponen el proveedor; el vínculo queda en la fila.
    const parts: string[] = [];
    for (const campo of technicalFields) {
      const value = descripcionDisplayValue(
        selecciones[campo.key],
        campo.opciones,
      );
      const part = formatDescripcionPart(
        value,
        campo.label,
        Boolean(campo.mostrarEtiquetaEnDescripcion),
      );
      if (part) parts.push(part);
    }
    if (observacion.trim()) parts.push(observacion.trim());
    return joinDescripcionParts(parts);
  }, [technicalFields, selecciones, observacion]);

  const resetForm = useCallback(() => {
    setSelecciones({});
    setEvalCampos([]);
    setRama(null);
    setProveedorId(0);
    setSucursalId(0);
    setDepositoId(0);
    setPrecioPreview(null);
    setClientErrors([]);
    setShowNewProveedor(false);
    setCatalogError(null);
  }, []);

  const seleccionesRef = useRef(selecciones);
  seleccionesRef.current = selecciones;

  const loadCatalog = useCallback(async () => {
    setLoadingCatalog(true);
    setCatalogError(null);
    try {
      // create y preview cargan el mismo catálogo + datos de stock (sucursal/depósito)
      // para paridad visual del bloque comercial; preview no hace POST de producto.
      // GET árbol no se traga con emptyOnForbidden: un 403 debe verse como
      // error de permiso (wizard vacío parecía «no hay alta»).
      const [arbolData, provs, sucs, deps] = await Promise.all([
        configuradorAPI.getArbol(false),
        // Lookup: Ventas/Presupuestos sin módulo Proveedores. list sigue para ABM.
        proveedoresAPI.lookup({ activo: true }).catch(emptyOnForbidden([] as Proveedor[])),
        opcionesAPI.listSucursales(),
        opcionesAPI.listDepositos(),
      ]);
      setArbol(arbolData);
      setProveedores(provs);
      setSucursales(sucs);
      setDepositos(deps);
    } catch (e) {
      setArbol(null);
      if (isForbiddenError(e)) {
        setCatalogError(MSG_CONFIGURADOR_CATALOG_FORBIDDEN);
        toast.error(MSG_CONFIGURADOR_CATALOG_FORBIDDEN);
        return;
      }
      console.error(e);
      setCatalogError("No se pudo cargar el catálogo del configurador.");
      toast.error("Error al cargar el configurador");
    } finally {
      setLoadingCatalog(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (!keepSelectionsOnReload.current) {
      resetForm();
    }
    keepSelectionsOnReload.current = false;
    void loadCatalog();
  }, [open, resetForm, loadCatalog]);

  // Preview: refrescar catálogo sin resetear selecciones al subir revision
  useEffect(() => {
    if (!open || !isPreview || catalogRevision <= 0) return;
    keepSelectionsOnReload.current = true;
    void (async () => {
      await loadCatalog();
      const current = seleccionesRef.current;
      const fab = String(current[KEY_FABRICA] || "");
      if (!fab) return;
      const seq = ++evalSeq.current;
      setEvaluating(true);
      try {
        const fab = String(current[KEY_FABRICA] || "");
        const result = await configuradorAPI.evaluar(
          current,
          false,
          perfilIdForFabricaKey(fab),
        );
        if (seq !== evalSeq.current) return;
        setEvalCampos(asEvalCampos(result.campos || []));
        setRama(result.rama ?? null);
      } catch (e) {
        console.error(e);
      } finally {
        if (seq === evalSeq.current) setEvaluating(false);
      }
    })();
  }, [catalogRevision, open, isPreview, loadCatalog]);

  const runEvaluar = useCallback(
    async (next: Record<string, unknown>, opts?: { keepOtrasManuals?: boolean }) => {
      const seq = ++evalSeq.current;
      // No deshabilitar inputs durante evaluar: disabled+FocusScope del Dialog/Sheet
      // roba el foco y Tab vuelve al primer campo.
      const focusKey =
        typeof document !== "undefined"
          ? document.activeElement?.getAttribute?.("data-config-field")
          : null;
      setEvaluating(true);
      try {
        const fab = String(next[KEY_FABRICA] || "");
        const result = await configuradorAPI.evaluar(
          next,
          false,
          perfilIdForFabricaKey(fab),
        );
        if (seq !== evalSeq.current) return null;
        const campos = asEvalCampos(result.campos || []);
        setEvalCampos(campos);
        setRama(result.rama ?? null);

        const visibleKeys = new Set(
          campos.filter(campoVisible).map((c) => c.key),
        );
        const fabrica = String(next[KEY_FABRICA] || "");
        const keepManuals =
          opts?.keepOtrasManuals ?? fabrica === "otras_fabricas";

        const cleaned: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(next)) {
          if (k === KEY_FABRICA || SPECIAL_KEYS.has(k)) {
            cleaned[k] = v;
            continue;
          }
          if (visibleKeys.has(k) || keepManuals) {
            cleaned[k] = v;
          }
        }
        if (!keepManuals) {
          for (const k of Object.keys(cleaned)) {
            if (SPECIAL_KEYS.has(k) || k === KEY_FABRICA) continue;
            if (!visibleKeys.has(k)) delete cleaned[k];
          }
        }
        setSelecciones(cleaned);
        return { result, cleaned, campos };
      } catch (e) {
        console.error(e);
        toast.error("Error al evaluar la configuración");
        return null;
      } finally {
        if (seq === evalSeq.current) setEvaluating(false);
        if (focusKey && typeof document !== "undefined") {
          // Solo restaurar si el foco se escapó (p. ej. unmount del campo).
          // Si el usuario ya Tab-eó a otro control, no robarle el foco.
          requestAnimationFrame(() => {
            const active = document.activeElement as HTMLElement | null;
            if (active?.getAttribute?.("data-config-field")) return;
            const el = document.querySelector(
              `[data-config-field="${CSS.escape(focusKey)}"]`,
            ) as HTMLElement | null;
            if (el && !el.hasAttribute("disabled")) {
              el.focus();
            }
          });
        }
      }
    },
    [],
  );

  const clearAfterCampo = useCallback(
    (campoKey: string, base: Record<string, unknown>) => {
      const campo = arbolByKey.get(campoKey);
      if (!campo) return { ...base };
      const next = { ...base };
      for (const c of arbol?.campos || []) {
        if (SPECIAL_KEYS.has(c.key)) continue;
        if (c.orden > campo.orden) delete next[c.key];
      }
      return next;
    },
    [arbol, arbolByKey],
  );

  const setFabrica = async (value: string) => {
    const next: Record<string, unknown> = value
      ? { [KEY_FABRICA]: value, [KEY_STOCK]: "0" }
      : {};
    setProveedorId(0);
    setSucursalId(0);
    setDepositoId(0);
    setPrecioPreview(null);
    setClientErrors([]);
    await runEvaluar(next, { keepOtrasManuals: value === "otras_fabricas" });
  };

  const setProveedorOtras = async (id: number) => {
    setProveedorId(id);
    setClientErrors([]);
    const next = { ...selecciones, [KEY_FABRICA]: "otras_fabricas" };
    if (id > 0) {
      next[KEY_PROVEEDOR_OTRAS] = { proveedor_id: id };
    } else {
      delete next[KEY_PROVEEDOR_OTRAS];
    }
    if (next[KEY_STOCK] == null) next[KEY_STOCK] = "0";
    await runEvaluar(next, { keepOtrasManuals: true });
  };

  const setTechnicalSelect = async (campoKey: string, opcionKey: string) => {
    let next = { ...selecciones, [campoKey]: opcionKey };
    next = clearAfterCampo(campoKey, next);
    setClientErrors([]);
    await runEvaluar(next, { keepOtrasManuals: false });
  };

  const setTechnicalManual = async (campoKey: string, manual: string) => {
    const next = { ...selecciones };
    if (!manual.trim()) {
      delete next[campoKey];
    } else {
      next[campoKey] = { manual: manual.trim() };
    }
    setClientErrors([]);
    setSelecciones(next);
  };

  const typedEvalTimer = useRef<number | null>(null);
  useEffect(() => {
    return () => {
      if (typedEvalTimer.current != null) {
        window.clearTimeout(typedEvalTimer.current);
      }
    };
  }, []);

  /**
   * Valor tipado (texto/número) en FAVRE: string plano + re-eval debounced
   * (deps value_gt_zero / clear de campos posteriores).
   */
  const setTechnicalTyped = (campoKey: string, value: string) => {
    setClientErrors([]);
    const optimistic = { ...seleccionesRef.current };
    if (!value.trim()) {
      delete optimistic[campoKey];
    } else {
      optimistic[campoKey] = value;
    }
    setSelecciones(optimistic);
    if (typedEvalTimer.current != null) {
      window.clearTimeout(typedEvalTimer.current);
    }
    typedEvalTimer.current = window.setTimeout(() => {
      let next = { ...seleccionesRef.current };
      if (!value.trim()) {
        delete next[campoKey];
      } else {
        next[campoKey] = value;
      }
      next = clearAfterCampo(campoKey, next);
      void runEvaluar(next, { keepOtrasManuals: false });
    }, 280);
  };

  const setComercial = (key: string, value: string) => {
    setSelecciones((prev) => ({ ...prev, [key]: value }));
    setClientErrors([]);
  };

  useEffect(() => {
    if (!open || !precioRaw.trim()) {
      setPrecioPreview(null);
      return;
    }
    const precioNum = toMoneyNumber(precioRaw);
    if (precioNum == null || !(precioNum >= 0)) {
      setPrecioPreview(null);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const preview = await configuradorAPI.precioPreview(precioNum);
        if (!cancelled) setPrecioPreview(preview);
      } catch {
        if (!cancelled) setPrecioPreview(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 280);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [precioRaw, open]);

  useEffect(() => {
    if (!stockGtZero) {
      setSucursalId(0);
      setDepositoId(0);
    }
  }, [stockGtZero]);

  const validateClient = (opts?: { soloTecnico?: boolean }): string[] => {
    const errors: string[] = [];
    if (!fabricaValue) {
      errors.push("Seleccioná FAVRE u OTRAS FABRICAS");
      return errors;
    }
    if (isOtras && !proveedorId) {
      errors.push("Seleccioná o creá un proveedor");
    }

    for (const campo of technicalFields) {
      const ev = evalByKey.get(campo.key);
      if (!ev || !campoVisible(ev)) continue;
      const raw = selecciones[campo.key];
      const hasVal =
        raw != null &&
        raw !== "" &&
        !(
          typeof raw === "object" &&
          raw !== null &&
          !(raw as Record<string, unknown>).manual &&
          !(raw as Record<string, unknown>).opcion_key &&
          !(raw as Record<string, unknown>).key &&
          !(raw as Record<string, unknown>).value
        );

      if (isFavre) {
        if (
          campoManualOk(ev) === false &&
          typeof raw === "object" &&
          raw &&
          (raw as { manual?: unknown }).manual
        ) {
          errors.push(`${campo.label}: no admite valor manual en FAVRE`);
        }
        if (campoRequired(ev) && !hasVal) {
          errors.push(`${campo.label} es obligatorio`);
        }
      }
    }

    if (opts?.soloTecnico) return errors;

    if (!precioRaw.trim()) {
      errors.push("Precio es obligatorio");
    } else {
      const p = toMoneyNumber(precioRaw);
      if (p == null || !(p > 0)) errors.push("Precio inválido");
    }
    if (!observacion.trim()) {
      errors.push("Observación fina (descripción) es obligatoria");
    }

    if (includeStock) {
      const stockStr = String(selecciones[KEY_STOCK] ?? "").trim();
      if (stockStr === "") {
        errors.push("Stock inicial es obligatorio (admite 0)");
      } else {
        const n = Number(stockStr.replace(",", "."));
        if (!Number.isFinite(n) || n < 0) {
          errors.push("Stock inicial inválido");
        } else if (n > 0 && (!sucursalId || !depositoId)) {
          errors.push("Con stock > 0 elegí Sucursal y Depósito");
        }
      }
    }

    return errors;
  };

  const buildSeleccionesPayload = (): Record<string, unknown> => {
    const payload: Record<string, unknown> = {
      [KEY_FABRICA]: fabricaValue,
    };

    for (const campo of technicalFields) {
      const raw = selecciones[campo.key];
      if (raw == null || raw === "") continue;
      if (typeof raw === "string") {
        payload[campo.key] = raw;
      } else if (typeof raw === "object") {
        const obj = raw as Record<string, unknown>;
        if (obj.manual != null && String(obj.manual).trim() !== "") {
          payload[campo.key] = { manual: String(obj.manual).trim() };
        } else if (obj.opcion_key || obj.key) {
          payload[campo.key] = String(obj.opcion_key || obj.key);
        }
      }
    }

    // Enviar canónico (1234.56); BE también acepta AR vía decimal_ar.
    const precioCanon = toMoneyNumber(precioRaw);
    payload[KEY_PRECIO] =
      precioCanon != null ? String(precioCanon) : precioRaw.trim();
    payload[KEY_OBS] = observacion.trim();
    // Presupuesto diferido: stock siempre 0 (no impacta inventario hasta materializar).
    payload[KEY_STOCK] = includeStock
      ? String(selecciones[KEY_STOCK] ?? "0").trim() || "0"
      : "0";

    if (includeStock && stockGtZero) {
      payload[KEY_SUCURSAL] = {
        sucursal_id: sucursalId,
        deposito_id: depositoId,
      };
    }

    if (isOtras && proveedorId) {
      payload[KEY_PROVEEDOR_OTRAS] = { proveedor_id: proveedorId };
    }

    return payload;
  };

  const reloadCatalogKeepSelections = useCallback(async () => {
    keepSelectionsOnReload.current = true;
    await loadCatalog();
    const current = seleccionesRef.current;
    const fab = String(current[KEY_FABRICA] || "");
    if (fab) {
      await runEvaluar(current, {
        keepOtrasManuals: fab === "otras_fabricas",
      });
    }
  }, [loadCatalog, runEvaluar]);

  return {
    mode,
    isPreview,
    includeStock,
    loadingCatalog,
    catalogError,
    evaluating,
    previewLoading,
    arbol,
    selecciones,
    setSelecciones,
    evalCampos,
    rama,
    proveedores,
    otrasProveedores,
    setProveedores,
    proveedorId,
    showNewProveedor,
    setShowNewProveedor,
    allowCreateProveedor,
    sucursales,
    depositos,
    sucursalId,
    setSucursalId,
    depositoId,
    setDepositoId,
    precioPreview,
    clientErrors,
    setClientErrors,
    fabricaOpciones,
    fabricaValue,
    isFavre,
    isOtras,
    evalByKey,
    technicalFields,
    availableDepositos,
    stockInicial,
    stockGtZero,
    precioRaw,
    observacion,
    descripcionPreview,
    resetForm,
    loadCatalog,
    reloadCatalogKeepSelections,
    runEvaluar,
    setFabrica,
    setProveedorOtras,
    setTechnicalSelect,
    setTechnicalManual,
    setTechnicalTyped,
    setComercial,
    validateClient,
    buildSeleccionesPayload,
  };
}

export type ConfiguradorProductoFormState = ReturnType<
  typeof useConfiguradorProductoForm
>;

export type ConfiguradorProductoFormBodyProps = {
  form: ConfiguradorProductoFormState;
  formDisabled?: boolean;
  /** Acciones extra en preview (Reiniciar / Actualizar). */
  previewActions?: boolean;
  onRestart?: () => void;
  onRefreshCatalog?: () => void;
  refreshingCatalog?: boolean;
  /** tecnico = hasta el último campo técnico. comercial = precio en adelante. */
  parte?: "completo" | "tecnico" | "comercial";
  idPrefix?: string;
  hideErrors?: boolean;
  /** En unificar, la observación se dibuja una sola vez al final. */
  ocultarObservacion?: boolean;
};

export function ConfiguradorProductoFormBody({
  form,
  formDisabled = false,
  previewActions = false,
  onRestart,
  onRefreshCatalog,
  refreshingCatalog = false,
  parte = "completo",
  idPrefix = "",
  hideErrors = false,
  ocultarObservacion = false,
}: ConfiguradorProductoFormBodyProps) {
  const {
    isPreview,
    includeStock,
    loadingCatalog,
    catalogError,
    evaluating,
    previewLoading,
    arbol,
    selecciones,
    setSelecciones,
    rama,
    otrasProveedores,
    proveedorId,
    setShowNewProveedor,
    allowCreateProveedor,
    sucursales,
    sucursalId,
    setSucursalId,
    depositoId,
    setDepositoId,
    precioPreview,
    clientErrors,
    fabricaOpciones,
    fabricaValue,
    isFavre,
    isOtras,
    evalByKey,
    technicalFields,
    availableDepositos,
    stockInicial,
    stockGtZero,
    precioRaw,
    observacion,
    descripcionPreview,
    setFabrica,
    setProveedorOtras,
    setTechnicalSelect,
    setTechnicalManual,
    setTechnicalTyped,
    setComercial,
  } = form;

  const showTecnico = parte !== "comercial";
  const showComercial = parte !== "tecnico";

  if (parte === "comercial" && (loadingCatalog || catalogError || !arbol || (arbol.campos || []).length === 0)) {
    return null;
  }

  if (loadingCatalog) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Cargando catálogo…
      </div>
    );
  }

  if (catalogError) {
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        {catalogError}
      </div>
    );
  }

  if (!arbol || (arbol.campos || []).length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        {MSG_CONFIGURADOR_CATALOG_EMPTY}
      </div>
    );
  }

  return (
    <div className="space-y-5 py-1">
      {showTecnico && isPreview && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
          <p className="font-medium text-amber-900 dark:text-amber-200">
            Vista previa — no crea productos
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Misma cascada FAVRE / Otras y bloque comercial (precio, IVA,
            observación{includeStock ? ", stock" : ""}) que el wizard real. Solo
            sandbox: no hay alta.
          </p>
        </div>
      )}

      {showTecnico && previewActions && (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={formDisabled || refreshingCatalog}
            onClick={onRestart}
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1" />
            Reiniciar selecciones
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={formDisabled || refreshingCatalog}
            onClick={onRefreshCatalog}
          >
            {refreshingCatalog ? (
              <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
            )}
            Actualizar catálogo
          </Button>
        </div>
      )}

      {showTecnico && isPreview && (rama || fabricaValue) && (
        <div className="flex flex-wrap gap-1.5">
          {rama && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs bg-muted text-muted-foreground">
              Rama: {rama === "favre" ? "FAVRE" : "Otras fábricas"}
            </span>
          )}
          {technicalFields.map((campo) => {
            const ev = evalByKey.get(campo.key);
            if (!ev) return null;
            const chips: string[] = [];
            if (campoRequired(ev) && isFavre) chips.push("requerido");
            if (campoManualOk(ev) && isOtras) chips.push("manual OK");
            if (chips.length === 0) return null;
            return (
              <span
                key={campo.key}
                className="inline-flex items-center px-2 py-0.5 rounded-md text-xs bg-muted text-muted-foreground"
              >
                {campo.label}: {chips.join(", ")}
              </span>
            );
          })}
        </div>
      )}

      {showTecnico ? (
      <>
      <div>
        <label className={labelClass} htmlFor={`${idPrefix}cfg-fabrica`}>
          Fábrica / Proveedor <span className="text-destructive">*</span>
        </label>
        <select
          id={`${idPrefix}cfg-fabrica`}
          data-config-field={KEY_FABRICA}
          className={inputClass}
          value={fabricaValue}
          disabled={formDisabled}
          onChange={(e) => setFabrica(e.target.value)}
        >
          <option value="">Seleccione…</option>
          {fabricaOpciones.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {isOtras && (
        <div>
          <label className={labelClass} htmlFor={`${idPrefix}cfg-proveedor`}>
            Proveedor{" "}
            {!isPreview && <span className="text-destructive">*</span>}
          </label>
          <div className="flex gap-2">
            <select
              id={`${idPrefix}cfg-proveedor`}
              data-config-field={KEY_PROVEEDOR_OTRAS}
              className={inputClass}
              value={proveedorId}
              disabled={formDisabled}
              onChange={(e) => setProveedorOtras(Number(e.target.value))}
            >
              <option value={0}>Seleccione un proveedor…</option>
              {otrasProveedores.map((p) => (
                <option key={p.id ?? p.codigo} value={p.id || 0}>
                  {p.nombreRazonSocial}
                  {p.codigo ? ` (${p.codigo})` : ""}
                </option>
              ))}
            </select>
            {!isPreview && allowCreateProveedor && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                disabled={formDisabled}
                onClick={() => setShowNewProveedor(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Nuevo
              </Button>
            )}
          </div>
          {otrasProveedores.length === 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              No hay proveedores activos.
              {!isPreview && allowCreateProveedor && " Creá uno para continuar."}
            </p>
          )}
        </div>
      )}

      {fabricaValue && technicalFields.length === 0 && !evaluating && (
        <p className="text-sm text-muted-foreground">
          {isFavre
            ? "No hay campos técnicos visibles para esta rama. Completá la secuencia o revisá el catálogo."
            : includeStock
              ? "Sin campos técnicos aplicables. Completá precio, observación y stock."
              : "Sin campos técnicos aplicables. Completá precio y observación."}
        </p>
      )}

      {technicalFields.map((campo) => {
        const ev = evalByKey.get(campo.key);
        const required = ev ? campoRequired(ev) && isFavre : false;
        const tipo = campo.tipo || ev?.tipo || "select";
        // Solo tipos con opciones → <select>. Antes, cualquier no-select
        // (p. ej. decimal_*) caía al else y renderizaba un selector vacío.
        const isSelect = campoTipoConOpciones(tipo);
        const isNumeric = campoTipoNumerico(tipo);
        const fieldId = `cfg-field-${idPrefix}${campo.key}`;
        const raw = selecciones[campo.key];
        const selectValue =
          typeof raw === "string"
            ? raw
            : typeof raw === "object" && raw
              ? String(
                  (raw as { opcion_key?: string; key?: string }).opcion_key ||
                    (raw as { key?: string }).key ||
                    "",
                )
              : "";
        const typedValue =
          typeof raw === "string"
            ? raw
            : typeof raw === "object" && raw && "manual" in raw
              ? String((raw as { manual?: unknown }).manual ?? "")
              : typeof raw === "object" && raw && "value" in raw
                ? String((raw as { value?: unknown }).value ?? "")
                : "";
        const manualValue =
          typeof raw === "object" && raw && "manual" in raw
            ? String((raw as { manual?: unknown }).manual ?? "")
            : typeof raw === "string" && isOtras
              ? raw
              : "";

        return (
          <div key={campo.key}>
            <label className={labelClass} htmlFor={fieldId}>
              {campo.label}
              {required ? (
                <span className="text-destructive"> *</span>
              ) : isOtras ? (
                <span className="text-muted-foreground font-normal">
                  {" "}
                  (opcional)
                </span>
              ) : null}
            </label>
            {isFavre && isSelect ? (
              <select
                id={fieldId}
                data-config-field={campo.key}
                className={inputClass}
                value={selectValue}
                disabled={formDisabled}
                onChange={(e) =>
                  setTechnicalSelect(campo.key, e.target.value)
                }
              >
                <option value="">Seleccione…</option>
                {(campo.opciones || [])
                  .filter((o) => o.activo !== false)
                  .map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))}
              </select>
            ) : isOtras ? (
              <input
                id={fieldId}
                data-config-field={campo.key}
                className={inputClass}
                value={manualValue}
                disabled={formDisabled}
                inputMode={isNumeric ? "decimal" : undefined}
                placeholder={
                  isNumeric
                    ? "Número (omitir si no aplica)"
                    : "Valor manual (omitir si no aplica)"
                }
                onChange={(e) =>
                  setTechnicalManual(campo.key, e.target.value)
                }
              />
            ) : isNumeric ? (
              <input
                id={fieldId}
                data-config-field={campo.key}
                className={inputClass}
                value={typedValue}
                disabled={formDisabled}
                inputMode="decimal"
                placeholder="0"
                onChange={(e) =>
                  setTechnicalTyped(campo.key, e.target.value)
                }
              />
            ) : (
              <input
                id={fieldId}
                data-config-field={campo.key}
                className={inputClass}
                value={typedValue}
                disabled={formDisabled}
                placeholder="Texto"
                onChange={(e) =>
                  setTechnicalTyped(campo.key, e.target.value)
                }
              />
            )}
          </div>
        );
      })}
      </>
      ) : null}

      {showComercial && fabricaValue && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={labelClass} htmlFor="cfg-precio">
                Precio <span className="text-destructive">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-muted-foreground">
                  $
                </span>
                <input
                  id="cfg-precio"
                  data-config-field={KEY_PRECIO}
                  className={`${inputClass} pl-7`}
                  inputMode="decimal"
                  value={precioRaw}
                  disabled={formDisabled}
                  onChange={(e) =>
                    setComercial(
                      KEY_PRECIO,
                      formatArgentineMoneyTyping(e.target.value),
                    )
                  }
                  placeholder="0,00"
                />
              </div>
            </div>
            <div>
              <label className={labelClass} htmlFor="cfg-iva">
                IVA (%)
              </label>
              <input
                id="cfg-iva"
                className={inputClass}
                value={precioPreview?.ivaPorcentaje ?? "21"}
                disabled
                readOnly
                tabIndex={-1}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="cfg-precio-final">
                Precio final (c/IVA)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-muted-foreground">
                  $
                </span>
                <input
                  id="cfg-precio-final"
                  className={`${inputClass} pl-7`}
                  value={
                    previewLoading
                      ? "…"
                      : precioPreview?.precioFinalConIva != null &&
                          precioPreview.precioFinalConIva !== ""
                        ? formatArgentineMoneyPreview(
                            precioPreview.precioFinalConIva,
                          )
                        : ""
                  }
                  disabled
                  readOnly
                  tabIndex={-1}
                  placeholder="Calculado"
                />
              </div>
            </div>
          </div>

          {ocultarObservacion ? null : (
            <div>
              <label className={labelClass} htmlFor="cfg-observacion">
                Observación fina (descripción){" "}
                <span className="text-destructive">*</span>
              </label>
              <textarea
                id="cfg-observacion"
                data-config-field={KEY_OBS}
                className={`${inputClass} min-h-[72px] resize-y`}
                value={observacion}
                disabled={formDisabled}
                onChange={(e) => setComercial(KEY_OBS, e.target.value)}
                placeholder="Descripción obligatoria del ítem"
              />
            </div>
          )}

          {ocultarObservacion ? null : (
          <div className="rounded-lg border border-border bg-muted/40 px-3 py-2">
            <p className="text-xs font-medium text-muted-foreground mb-1">
              Vista previa descripción
            </p>
            <p className="text-sm break-words">
              {descripcionPreview || (
                <span className="text-muted-foreground">
                  Se arma con los atributos y la observación…
                </span>
              )}
            </p>
            {evaluating && (
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Actualizando campos…
              </p>
            )}
            {rama && !isPreview && (
              <p className="text-xs text-muted-foreground mt-1">
                Rama: {rama === "favre" ? "FAVRE" : "Otras fábricas"}
              </p>
            )}
          </div>
          )}

          {includeStock && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className={labelClass} htmlFor="cfg-stock">
                  Stock inicial <span className="text-destructive">*</span>
                </label>
                <NumberField
                  id="cfg-stock"
                  data-config-field={KEY_STOCK}
                  min={0}
                  step={1}
                  className={inputClass}
                  value={stockInicial}
                  disabled={formDisabled}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelecciones((prev) => ({
                      ...prev,
                      [KEY_STOCK]: v === "" ? "0" : v,
                    }));
                  }}
                  onKeyDown={(e) => {
                    if (
                      e.key === "." ||
                      e.key === "," ||
                      e.key === "e" ||
                      e.key === "E"
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
              {stockGtZero && (
                <>
                  <div>
                    <label className={labelClass} htmlFor="cfg-sucursal">
                      Sucursal <span className="text-destructive">*</span>
                    </label>
                    <select
                      id="cfg-sucursal"
                      data-config-field="sucursal_id"
                      className={inputClass}
                      value={sucursalId}
                      disabled={formDisabled}
                      onChange={(e) => {
                        setSucursalId(Number(e.target.value));
                        setDepositoId(0);
                      }}
                    >
                      <option value={0}>Seleccione una sucursal</option>
                      {sucursales.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass} htmlFor="cfg-deposito">
                      Depósito <span className="text-destructive">*</span>
                    </label>
                    <select
                      id="cfg-deposito"
                      data-config-field="deposito_id"
                      className={inputClass}
                      value={depositoId}
                      disabled={formDisabled || !sucursalId}
                      onChange={(e) => setDepositoId(Number(e.target.value))}
                    >
                      <option value={0}>Seleccione un depósito</option>
                      {availableDepositos.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
          )}
          {!includeStock && (
            <p className="text-xs text-muted-foreground">
              En presupuesto no se carga stock inicial: el inventario se define
              al materializar / confirmar la venta.
            </p>
          )}
        </>
      )}

      {!hideErrors && !isPreview && clientErrors.length > 0 && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive space-y-1">
          {clientErrors.map((err) => (
            <p key={err}>{err}</p>
          ))}
        </div>
      )}
    </div>
  );
}
